import type { Id } from "@convex/_generated/dataModel";
import { Info, PlusCircle, Trash2 } from "lucide-react";
import type { UseFormReturn } from "react-hook-form";
import { toast } from "sonner";

import { Button } from "~/components/ui/button";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import {
  type PosDevicePaymentTerminal,
  paymentTypeOptions,
} from "~/lib/validators/pos-device-payment-terminal";

interface PosDevice {
  name: string;
  paymentTerminalActive: boolean;
  paymentTerminal?: PosDevicePaymentTerminal;
}

interface DevicePaymentTerminalData {
  _id: Id<"posDevicePaymentTerminals">;
  type: "stripe";
  readerId?: string;
  locationId?: string;
  posDeviceId?: Id<"posDevices">;
}

interface DeviceCardPaymentsProps {
  form: UseFormReturn<PosDevice>;
  isLoading: boolean;
  existingTerminal?: DevicePaymentTerminalData | null;
  onAddTerminal: () => void;
  onRemoveTerminal: () => void;
}

export function DeviceCardPayments({
  form,
  isLoading,
  existingTerminal,
  onAddTerminal,
  onRemoveTerminal,
}: DeviceCardPaymentsProps) {
  const values = form.watch();
  const terminalInForm = values.paymentTerminalActive && values.paymentTerminal;
  const terminalInDB = existingTerminal;

  if (!(terminalInForm || terminalInDB)) {
    return (
      <>
        <div className="bg-info/10 flex items-center space-x-2 rounded-xl px-4 py-3">
          <Info className="text-info size-5" />
          <p className="body-sm text-info">
            Connect a payment terminal to enable card payments for this device.
          </p>
        </div>
        <Button
          className="flex w-full items-center space-x-1.5 px-5 py-3"
          onClick={onAddTerminal}
          type="button"
          variant="outline"
        >
          <PlusCircle className="size-4" />
          <p>Add Payment Terminal</p>
        </Button>
      </>
    );
  }

  const terminalType =
    terminalInDB?.type ??
    (terminalInForm ? values.paymentTerminal?.type : null);

  return (
    <>
      <div className="bg-muted flex w-full justify-between rounded-xl p-3">
        <div className="flex items-center space-x-2">
          <img
            alt={
              terminalType
                ? `${paymentTypeOptions[terminalType].title} logo`
                : "Terminal logo"
            }
            height={24}
            src={terminalType ? paymentTypeOptions[terminalType].logo : ""}
            style={{ width: "42px", height: "auto" }}
            width={42}
          />
          <p className="subtitle-sm text-foreground">
            {terminalType
              ? paymentTypeOptions[terminalType].title
              : "Payment Terminal"}
          </p>
        </div>
        <div className="flex space-x-1.5">
          {terminalInDB && (
            <Button
              className="flex items-center space-x-1.5"
              onClick={onRemoveTerminal}
              type="button"
              variant="outline"
            >
              <Trash2 className="size-4" />
              <p>Remove Payment Terminal</p>
            </Button>
          )}
          {!terminalInDB && terminalInForm && (
            <Button
              className="text-destructive hover:bg-destructive/10 hover:text-destructive"
              onClick={() => {
                form.reset({
                  name: form.getValues().name,
                  paymentTerminalActive: false,
                });

                toast.success("Payment terminal removed", {
                  description:
                    "Payment terminal configuration has been removed.",
                });
              }}
              type="button"
              variant="ghost"
            >
              Remove Payment Terminal
            </Button>
          )}
        </div>
      </div>
      {terminalType === "stripe" && (
        <div className="flex flex-col space-y-4">
          <FormField
            control={form.control}
            name="paymentTerminal.locationId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Stripe Terminal Location ID</FormLabel>
                <FormControl>
                  <Input {...field} disabled={isLoading} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      )}
    </>
  );
}
